import type { Application, ApplicationNote } from '~/types'

export const useApplicationsService = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase as string
    const { logAction } = useActivityLog()

    const getApplications = async (): Promise<Application[]> => {
        const token = useCookie('auth_token')
        const data = await $fetch<Application[]>(`${apiBase}/applications`, {
            headers: { Authorization: `Bearer ${token.value}` }
        })
        return data
    }

    const getApplication = async (id: string): Promise<Application | undefined> => {
        const token = useCookie('auth_token')
        try {
            const data = await $fetch<Application>(`${apiBase}/applications/${id}`, {
                headers: { Authorization: `Bearer ${token.value}` }
            })
            return data
        } catch {
            return undefined
        }
    }

    const updateStatus = async (id: string, status: string): Promise<void> => {
        const token = useCookie('auth_token')
        await $fetch(`${apiBase}/applications/${id}/status`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token.value}` },
            body: { status }
        })

        logAction({
            action: 'UPDATE',
            entityType: 'Candidature',
            entityId: id,
            details: `Statut modifié à ${status}`,
        })
    }

    const updateStage = async (id: string, stage: number): Promise<void> => {
        const token = useCookie('auth_token')
        await $fetch(`${apiBase}/applications/${id}/stage`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token.value}` },
            body: { stage }
        })

        const stageNames = ['Réception', 'Lecture du profil', 'Examen initial', 'Analyse en cours', 'Entretien', 'Retour entretien', 'Délibération', 'Validation']
        logAction({
            action: 'UPDATE',
            entityType: 'Candidature',
            entityId: id,
            details: `Étape modifiée à ${stageNames[stage] || stage}`,
        })
    }

    const addNote = async (id: string, content: string): Promise<ApplicationNote> => {
        const token = useCookie('auth_token')
        const note = await $fetch<ApplicationNote>(`${apiBase}/applications/${id}/notes`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token.value}` },
            body: { content }
        })

        logAction({
            action: 'CREATE',
            entityType: 'Candidature',
            entityId: id,
            details: `Note ajoutée`,
        })

        return note
    }

    const deleteNote = async (applicationId: string, noteId: string): Promise<void> => {
        const token = useCookie('auth_token')
        await $fetch(`${apiBase}/applications/${applicationId}/notes/${noteId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token.value}` }
        })

        logAction({
            action: 'DELETE',
            entityType: 'Candidature',
            entityId: applicationId,
            details: `Note supprimée`,
        })
    }

    const deleteApplication = async (id: string): Promise<void> => {
        const token = useCookie('auth_token')
        await $fetch(`${apiBase}/applications/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token.value}` }
        })

        logAction({
            action: 'DELETE',
            entityType: 'Candidature',
            entityId: id,
            details: `Candidature supprimée`,
        })
    }

    const reopenApplication = async (id: string): Promise<void> => {
        const token = useCookie('auth_token')
        await $fetch(`${apiBase}/applications/${id}/reopen`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token.value}` }
        })

        logAction({
            action: 'UPDATE',
            entityType: 'Candidature',
            entityId: id,
            details: `Dossier réouvert`,
        })
    }

    return {
        getApplications,
        getApplication,
        updateStatus,
        updateStage,
        addNote,
        deleteNote,
        deleteApplication,
        reopenApplication
    }
}

